import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a01f8d12-1bde-4fd2-8a5f-4dafd263008a/artifacts/d665bj3h_Global%20Wings%20fond%20noir.png";

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', fullName: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const user = await login(loginData.email, loginData.password);
      toast.success('Connexion réussie!');
      // Redirect based on role
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'agent') {
        navigate('/agent');
      } else if (user.role === 'reception') {
        navigate('/reception');
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await register(registerData.email, registerData.password, registerData.fullName);
      toast.success('Compte créé!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-6 relative">
      <div className="absolute inset-0 gradient-radial opacity-30" />
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      
      <div className="w-full max-w-md relative">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/" className="flex justify-center mb-10">
            <img src={LOGO_URL} alt="Global Wings" className="h-32" />
          </Link>

          <div className="card-luxury p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-white mb-2">Bienvenue</h1>
              <p className="text-[#64748B]">Connectez-vous ou créez un compte</p>
            </div>

            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8 bg-white/5 rounded-full p-1">
                <TabsTrigger value="login" className="rounded-full data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B1121]" data-testid="login-tab">Connexion</TabsTrigger>
                <TabsTrigger value="register" className="rounded-full data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B1121]" data-testid="register-tab">Inscription</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                  <div>
                    <Label className="text-[#94A3B8]">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input type="email" value={loginData.email} onChange={(e) => setLoginData({...loginData, email: e.target.value})} required
                        className="pl-11 bg-white/5 border-white/10 text-white focus:border-[#D4AF37] h-12" placeholder="votre@email.com" data-testid="login-email" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#94A3B8]">Mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input type="password" value={loginData.password} onChange={(e) => setLoginData({...loginData, password: e.target.value})} required
                        className="pl-11 bg-white/5 border-white/10 text-white focus:border-[#D4AF37] h-12" placeholder="••••••••" data-testid="login-password" />
                    </div>
                  </div>
                  <div className="text-right">
                    <Link to="/forgot-password" className="text-sm text-[#D4AF37] hover:underline">
                      Mot de passe oublié ?
                    </Link>
                  </div>
                  <button type="submit" className="w-full btn-gold py-4 flex items-center justify-center gap-2" disabled={isLoading} data-testid="login-submit">
                    {isLoading ? 'Connexion...' : (<>Se Connecter<ArrowRight className="w-4 h-4" /></>)}
                  </button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                  <div>
                    <Label className="text-[#94A3B8]">Nom Complet</Label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input value={registerData.fullName} onChange={(e) => setRegisterData({...registerData, fullName: e.target.value})} required
                        className="pl-11 bg-white/5 border-white/10 text-white focus:border-[#D4AF37] h-12" placeholder="Votre nom" data-testid="register-name" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#94A3B8]">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input type="email" value={registerData.email} onChange={(e) => setRegisterData({...registerData, email: e.target.value})} required
                        className="pl-11 bg-white/5 border-white/10 text-white focus:border-[#D4AF37] h-12" placeholder="votre@email.com" data-testid="register-email" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#94A3B8]">Mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                      <Input type="password" value={registerData.password} onChange={(e) => setRegisterData({...registerData, password: e.target.value})} required minLength={6}
                        className="pl-11 bg-white/5 border-white/10 text-white focus:border-[#D4AF37] h-12" placeholder="••••••••" data-testid="register-password" />
                    </div>
                  </div>
                  <button type="submit" className="w-full btn-gold py-4 flex items-center justify-center gap-2" disabled={isLoading} data-testid="register-submit">
                    {isLoading ? 'Création...' : (<>Créer un Compte<ArrowRight className="w-4 h-4" /></>)}
                  </button>
                </form>
              </TabsContent>
            </Tabs>

            <div className="mt-8 text-center">
              <Link to="/" className="text-sm text-[#64748B] hover:text-[#D4AF37] transition-colors">Retour à l'accueil</Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
